import { faList, faListOl, faParagraph } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { chainCommands } from "prosemirror-commands";
import { type DOMOutputSpec, type ParseRule, Node as ProseMirrorNode, Schema } from "prosemirror-model";
import { EditorState, Plugin, PluginKey } from "prosemirror-state";
import { Decoration, DecorationSet, EditorView } from "prosemirror-view";

import { BlockActionMenu, getUser } from "~/lib/prose/blockActions";
import { blockActionsEnabled } from "~/lib/prose/plugins/blockActions";
import { getContext } from "~/lib/prose/plugins/context";
import { promptResponseAttrs } from "~/lib/prose/plugins/prompt";
import { toAlphabeticalOrder, toRomanOrder } from "~/lib/utils";
import {
	getCommentMenuItem,
	getCopyLinkMenuItem,
	getCreatorMenuItem,
	getDeleteBlockMenu,
	getReactionMenuItem,
} from "~/notes/AssignableDropdown";

import { mergeToPreviousEmptyBlock, removeEmptyBlockBefore, setBlockType, setUnstyled } from "../commands";
import { NodeExtension } from "../extension";
import { ASSIGNABLE_TYPE_NAMES } from "../plugins/assignable";
import { BlockTreeNode, getBlockBoundaryState } from "../plugins/blockBoundary";
import { blockTypeShortcut } from "../plugins/inputRules";
import type { getPosHandler } from "../types";
import { isNodeAssignable } from "../utils";

function getListCounter(num: number, style: "a" | "1" | "i") {
	switch (style) {
		case "a":
			return toAlphabeticalOrder(num);
		case "i":
			return toRomanOrder(num);
		default:
			return num.toString();
	}
}

class PlainTextNodeBase extends NodeExtension {
	get name() {
		throw Error("Can't use PlainText base node. Use one of the sub types");
		return "";
	}

	get domParseRules(): ParseRule[] {
		return [];
	}

	get node() {
		const self = this;
		return {
			content: "inline*",
			group: "block",
			attrs: {
				blockId: { default: null },
				done: { default: false },
				assignees: { default: [] },
				creator: { default: null },
				indent: { default: 0 },
				...promptResponseAttrs,
			},
			defining: true,
			parseDOM: [
				{
					tag: `[data-block-type="${self.name}"]`,
					getAttrs: (dom: Node | string) =>
						dom instanceof Element
							? {
									indent: parseInt(dom.getAttribute("data-indent") || "0", 10),
									creator: dom.getAttribute("data-creator"),
								}
							: null,
				},
				...this.domParseRules,
				{
					tag: "p",
					priority: 0,
				},
			],
			toDOM(node: ProseMirrorNode): DOMOutputSpec {
				return [
					"p",
					{
						"data-block-id": node.attrs["blockId"],
						"data-block-type": self.name,
						"data-indent": node.attrs.indent ? node.attrs.indent.toString() : 0,
						"data-creator": node.attrs.creator,
					},
					0,
				];
			},
		};
	}

	view = (node: ProseMirrorNode, view: EditorView, getPos: getPosHandler, _decorations: readonly Decoration[]) => {
		const self = this;
		const dom = document.createElement("p");
		function setDomAttribute(node: ProseMirrorNode) {
			dom.setAttribute("data-block-id", node.attrs["blockId"]);
			dom.setAttribute("data-block-type", self.name);
			dom.setAttribute("data-indent", node.attrs.indent ? node.attrs.indent.toString() : 0);
			dom.setAttribute("data-creator", node.attrs["creator"]);
			if (self.options.showBullet && self.name === "unstyled") {
				dom.setAttribute("data-show-bullet", "true");
			}
		}
		let blockActionsMountedForNode: ProseMirrorNode | null = null;
		function mountBlockActions(node: ProseMirrorNode) {
			if (blockActionsMountedForNode?.sameMarkup(node)) {
				return;
			}
			if (blockActionsMountedForNode) {
				unmountBlockActions();
			}
			const context = getContext(view.state);
			if (context?.isCustomSection && !isNodeAssignable(node)) return;
			const { creator: creatorId, blockId } = node.attrs;
			const creator = context?.participants?.find((p: { id: string }) => p.id === creatorId);

			if (typeof getPos === "boolean") throw new Error("getPos is a bool for a node");
			const blockActions = new BlockActionMenu({
				node,
				view,
				menuSections: () => [
					{
						children: context.isMeetingModal
							? [getReactionMenuItem({ blockKey: blockId, editorView: view })]
							: !context.isPrivateNote
								? [
										getReactionMenuItem({ blockKey: blockId, editorView: view }),
										getCommentMenuItem({ view, blockKey: blockId }),
									]
								: [],
					},
					{
						children: [
							...(context?.noteId ? [getCopyLinkMenuItem({ noteId: context.noteId, blockKey: blockId })] : []),
							getDeleteBlockMenu({ view, getPos, text: "Delete" }),
						],
					},
					{ children: !context?.isPrivateNote ? [getCreatorMenuItem({ user: getUser(), creator })] : [] },
				],
				triggers: [],
			});
			dom.appendChild(blockActions.el);
			blockActionsMountedForNode = node;
		}
		function unmountBlockActions() {
			dom.querySelector("[data-block-actions-container]")?.remove();
			blockActionsMountedForNode = null;
		}
		dom.setAttribute("pmblock", "");
		setDomAttribute(node);
		const contentWrapper = document.createElement("div");
		contentWrapper.setAttribute("pmcontentWrapper", "");
		const contentDOM = document.createElement("div");
		contentDOM.setAttribute("pmcontent", "");
		dom.appendChild(contentWrapper);
		contentWrapper.appendChild(contentDOM);

		const shouldShowBlockActions = node.content.size !== 0 && blockActionsEnabled(view.state);

		if (shouldShowBlockActions) {
			mountBlockActions(node);
		}

		let lastNode = node;

		return {
			dom,
			contentDOM,
			update: (newNode: ProseMirrorNode, _newDecorations: readonly Decoration[]) => {
				if (newNode.type !== lastNode.type) return false;
				const shouldShowBlockActions = newNode.content.size !== 0 && blockActionsEnabled(view.state);
				if (!newNode.sameMarkup(lastNode)) {
					setDomAttribute(newNode);
				}
				if (shouldShowBlockActions) {
					mountBlockActions(newNode);
				} else {
					unmountBlockActions();
				}
				lastNode = newNode;
				return true;
			},
			ignoreMutation: (
				record:
					| MutationRecord
					| {
							type: "selection";
							target: Element;
					  },
			) => {
				if (record.type === "selection" && contentDOM === record.target) {
					return false;
				}
				if (record.type !== "selection" && !contentWrapper?.contains(record.target)) {
					return true;
				}
				if (record.type == "characterData" || record.type == "childList") {
					return false;
				}
				return true;
			},
		};
	};
}

export class BulletPoint extends PlainTextNodeBase {
	get name() {
		return "bullet-point";
	}
	get domParseRules() {
		return [
			{
				tag: 'li[data-list-type="UL"]',
				node: "bullet-point",
				priority: 1,
				getAttrs: (dom: Node | string) =>
					dom instanceof Element
						? {
								indent: parseInt(dom.getAttribute("data-indent") || "0", 10),
							}
						: null,
			},
		];
	}
	menuEntry() {
		return {
			icon: faList,
			title: "Bullet list",
			subtitle: "Add a bulleted list.",
			run: () => {
				return setBlockType(this.$type);
			},
			section: "types" as const,
			priority: 2,
			hideOnExtraSmallScreen: true,
			keyboardShortcut: "-",
		};
	}
}

type NumberedListPluginState = {
	decoSet: DecorationSet;
};

type NumberedListProps = {
	"data-level": string;
	"data-list-counter": string;
};

export class NumberedListItem extends PlainTextNodeBase {
	static numberedListPluginKey = new PluginKey<NumberedListPluginState>("numbered-list");
	static makeNumberedListPlugin = () => {
		const getNumberedListProps = (state: EditorState) => {
			const blockNumberedListProps: { [key: string]: NumberedListProps } = {};
			const blockTree = getBlockBoundaryState(state)?.blockTree;
			if (!blockTree) return blockNumberedListProps;
			function getTypeLevel(node: BlockTreeNode): number {
				if ("numberedListLevel" in node.meta) {
					return node.meta["numberedListLevel"];
				}
				if (node.block?.type.name !== "numbered-list-item") return 0;
				if (node.prevNodeOfSameType) {
					if (node.prevNodeOfSameType.indent !== node.indent) {
						return (node.meta["numberedListLevel"] = 1);
					}
					return (node.meta["numberedListLevel"] = getTypeLevel(node.prevNodeOfSameType));
				}
				if (node.parent) {
					return (node.meta["numberedListLevel"] = getTypeLevel(node.parent) + 1);
				}
				return (node.meta["numberedListLevel"] = 1);
			}
			function visit(node: BlockTreeNode) {
				if (!node.block || node.block.type.name !== "numbered-list-item") return;
				const prevNumberedListItem = node.prevNodeOfSameTypeAndIndent;
				let counter = 1;
				if (prevNumberedListItem) {
					counter = (prevNumberedListItem.meta["counter"] || 0) + 1;
				}
				const typeLevel = getTypeLevel(node);
				blockNumberedListProps[node.block.attrs.blockId] = {
					"data-list-counter": getListCounter(counter, typeLevel === 2 ? "a" : typeLevel > 2 ? "i" : "1"),
					"data-level": typeLevel.toString(),
				};
				node.meta["counter"] = counter;
			}
			for (const node of blockTree.root.descendants()) {
				visit(node);
			}
			return blockNumberedListProps;
		};
		const makeDecorationSet = (state: EditorState) => {
			// It might be faster to diff result of getNumberedListProps() and change the current decorationSet.
			const decos: Decoration[] = [];
			const blockBoundaries = getBlockBoundaryState(state)?.blockBoundaries;
			if (!blockBoundaries) return DecorationSet.create(state.doc, []);
			Object.entries(getNumberedListProps(state)).forEach(([k, v]: [string, NumberedListProps]) => {
				if (!(k in blockBoundaries)) return;
				const [start, end] = blockBoundaries[k];
				const deco = Decoration.node(start, end, v);
				decos.push(deco);
			});
			return DecorationSet.create(state.doc, decos);
		};
		return new Plugin({
			key: NumberedListItem.numberedListPluginKey,
			state: {
				init: (_config, state) => {
					const decoSet = makeDecorationSet(state);
					return { decoSet };
				},
				apply: (tr, value, _old, newState) => {
					if (tr.docChanged) {
						return { ...value, decoSet: makeDecorationSet(newState) };
					}
					return value;
				},
			},
			props: {
				decorations: state => {
					return NumberedListItem.numberedListPluginKey.getState(state)?.decoSet;
				},
				transformPastedHTML: (html: string) => {
					const dom = document.implementation.createHTMLDocument("");
					const container = dom.createElement("div");
					dom.documentElement.innerHTML = html;
					const bodyHTML = dom.body.innerHTML;
					container.innerHTML = bodyHTML;
					dom.body.innerHTML = "";
					dom.body.appendChild(container);
					const listContainers: Element[] = [];
					function processNode(node: Element | Node, depth = 0, listType: string) {
						if (!(node instanceof Element)) return;
						if (node.tagName === "UL" || node.tagName === "OL") {
							listContainers.push(node);
							if (node.hasChildNodes()) {
								node.childNodes.forEach(c => {
									processNode(c, depth + 1, node.tagName);
								});
							}
							if (node.nextSibling) {
								processNode(node.nextSibling, depth, listType);
							}
							return;
						}
						let indentFromAttr: number | null = null;
						try {
							// slack specific, which starts at 0.
							let attrValue = node.getAttribute("data-stringify-indent"); // this is slack specific
							if (attrValue) {
								indentFromAttr = parseInt(attrValue, 10) + 1;
							}
						} catch (_error) {}
						if (node.tagName === "LI" && listType && !node.hasAttribute("data-fellow-serialized-list")) {
							if (indentFromAttr) {
								node.setAttribute("data-indent", indentFromAttr.toString());
							} else {
								node.setAttribute("data-indent", depth.toString());
							}
							node.setAttribute("data-list-type", listType);
						}
						if (node.hasChildNodes()) {
							node.childNodes.forEach(c => {
								processNode(c, depth, listType);
							});
						}
						if (node.nextSibling) {
							processNode(node.nextSibling, depth, listType);
						}
					}
					processNode(container, 0, "");
					function removeContainer(node: Element) {
						node.replaceWith(...Array.from(node.childNodes));
					}
					listContainers.forEach(listContainer => {
						removeContainer(listContainer);
					});
					return container.innerHTML;
				},
			},
		});
	};
	get name() {
		return "numbered-list-item";
	}
	get domParseRules() {
		return [
			{
				tag: 'li[data-list-type="OL"]',
				node: "numbered-list-item",
				preserveWhitespace: "full",
				priority: 1,
				getAttrs: (dom: Node | string) => {
					const res =
						dom instanceof Element
							? {
									indent: parseInt(dom.getAttribute("data-indent") || "0", 10),
								}
							: null;
					return res;
				},
			} as const,
		];
	}
	get plugins() {
		if (this.options.hideListItemNumber) return [];
		return [NumberedListItem.makeNumberedListPlugin()];
	}
	menuEntry() {
		return {
			icon: faListOl,
			title: "Number List",
			subtitle: "Add a numbered list.",
			run: () => {
				return setBlockType(this.$type);
			},
			section: "types" as const,
			priority: 2,
			hideOnExtraSmallScreen: true,
			keyboardShortcut: "1.",
		};
	}
	inputRules() {
		if (this.options.disableIndentInputRule) {
			return [];
		}
		const schema = this.editorView?.state.schema as Schema;
		if (!schema) return [];
		return [
			blockTypeShortcut(
				"\\d{1}\\.",
				schema.nodes["numbered-list-item"] || schema.nodes["unstyled"],
				(_, node) => {
					let indent = Math.max(node.attrs.indent || 0, 1);
					if (ASSIGNABLE_TYPE_NAMES.includes(node.type.name)) {
						indent = node.attrs.indent + 2;
					}
					return { indent };
				},
				{
					predicate: node =>
						ASSIGNABLE_TYPE_NAMES.includes(node.type.name) ||
						(PLAIN_TEXT_NODE_TYPE_NAMES.includes(node.type.name) && node.type.name !== "numbered-list-item"),
				},
			),
		];
	}
}

export class Unstyled extends PlainTextNodeBase {
	get name() {
		return "unstyled";
	}
	keys() {
		return {
			"Mod-Backspace": setUnstyled(false),
			Backspace: chainCommands(setUnstyled(true), removeEmptyBlockBefore(), mergeToPreviousEmptyBlock()),
		};
	}
	menuEntry() {
		return {
			icon: faParagraph,
			title: "Paragraph",
			subtitle: "Unstyled simple text.",
			run: () => {
				return setBlockType(this.$type);
			},
			section: "types" as const,
			priority: 2,
		};
	}
	inputRules() {
		if (this.options.disableIndentInputRule) {
			return [];
		}
		const schema = this.editorView?.state.schema as Schema;
		if (!schema) return [];
		return [
			blockTypeShortcut(
				"[*-]",
				schema.nodes["bullet-point"] || this.$type,
				(_, node) => {
					let indent = Math.max(node.attrs.indent || 0, 1);
					if (ASSIGNABLE_TYPE_NAMES.includes(node.type.name)) {
						indent = node.attrs.indent + 2;
					}
					return { indent };
				},
				{
					predicate: node =>
						ASSIGNABLE_TYPE_NAMES.includes(node.type.name) ||
						(PLAIN_TEXT_NODE_TYPE_NAMES.includes(node.type.name) && node.type.name !== "bullet-point"),
				},
			),
		];
	}
}

export const PLAIN_TEXT_NODE_TYPE_NAMES: readonly string[] = ["unstyled", "bullet-point", "numbered-list-item"];
export const LIST_ITEM_TYPE_NAMES: readonly string[] = ["bullet-point", "numbered-list-item"];
